import React, { createContext, useContext, useMemo } from 'react';

import { useSession } from './SessionContext';
import { useStaticProps } from './StaticPropsContext';
import { useQueryParameter } from './QueryParameterContext';
import { useEventTracking } from '~/hooks';
import { queryParametersSchema } from '~/schema';
import { Intake, IntakeSearchQuery, useIntakes } from '~/data';

export interface SearchResult {
  intakes: Array<Intake>;
  totalIntakes: number;

  isPreviousIntakesData: boolean;
  isLoadingIntakes: boolean;
  isFetchingIntakes: boolean;
  isIntakesError: boolean;
}

const searchResultContext = createContext<SearchResult | undefined>(undefined);

export const SearchResultContextProvider = ({ children }: { children: React.ReactNode }) => {
  const sessionId = useSession();
  const staticProps = useStaticProps();
  const { trackEvent } = useEventTracking('Search Result');
  const { queryParameters, rnd, queryId } = useQueryParameter();

  const searchQueryParams = useMemo(() => {
    const params = {
      'page[number]': 1,
      'page[size]': 1,
      'filter[q]': '',
      'meta[rnd]': rnd,
      'meta[query_id]': queryId,
      'meta[session_id]': sessionId,
    } as IntakeSearchQuery;
    queryParametersSchema.parameters.forEach((parameter) => {
      const value = parameter.getValue({ staticProps, queryParameters });
      const defaultValue = parameter.getDefaultValue({ initialQueryParameters: staticProps.initialQueryParameter });
      const result = parameter.toSearchQuery({
        value,
        staticProps,
        defaultValue,
        queryParameters,
      });
      if (!result) return;
      Object.assign(params, result);
    });
    return params;
  }, [staticProps, queryParameters, rnd, queryId, sessionId]);

  const { intakes, totalIntakes, isLoadingIntakes, isFetchingIntakes, isPreviousIntakesData, isIntakesError } =
    useIntakes(searchQueryParams, {
      onSuccess: (data, total) => {
        trackEvent({
          event: 'Results Returned',
          data: {
            rnd,
            queryId,
            page_total_intakes: data.length,
            overall_total_intakes: total,
            queryParameters: queryParameters,
          },
        });
      },
    });

  const value = useMemo(
    () => ({
      intakes,
      totalIntakes,
      isPreviousIntakesData,
      isLoadingIntakes,
      isFetchingIntakes,
      isIntakesError,
    }),
    [intakes, isPreviousIntakesData, totalIntakes, isLoadingIntakes, isFetchingIntakes, isIntakesError]
  );

  return <searchResultContext.Provider value={value}>{children}</searchResultContext.Provider>;
};

export const useSearchResult = () => {
  const value = useContext(searchResultContext);
  if (value === undefined) {
    throw new Error('useSearchResult must be used within a SearchResultContextProvider');
  }
  return value;
};
